"use client"
import styled from "styled-components"
import { StyledPageWrapper } from "theme/shared"
import { TABLE_SCROLL_CLASS } from "utils/contentPageHtml"

/**
 * Renders a single CMS-authored rich text block for the fixed content pages
 * (DLK-911). Deliberately adds no heading of its own — the page heading is
 * authored inside the rich text, and heading levels are rendered as authored.
 */

const StyledPageDiv = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  display: flex;
  align-items: center;
  background: ${(p) => p.theme.colors.lightGrey};
  padding-bottom: 80px;
`

const StyledTextWrapper = styled.div`
  margin-top: 40px;
  /* Needed because the parent centres its children: without a definite width
     this box shrinks to fit, so a wide table would stretch it past the viewport
     instead of scrolling inside its own container. */
  width: 100%;
  max-width: 1400px;
  padding-right: 150px;
  padding-left: 150px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    padding-right: 20px;
    padding-left: 20px;
  }
`

const StyledContent = styled.div`
  width: 100%;
  max-width: 1160px;
  padding: 0 20px;
  margin: 40px auto 0;

  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 0 40px;
  }

  h1,
  h2,
  h3,
  h4 {
    color: ${(p) => p.theme.colors.black};
    margin: 40px 0 0;
    &:first-child {
      margin-top: 0;
    }
  }

  h1 {
    font-size: 28px;
    font-weight: 500;
    line-height: 140%;
    letter-spacing: 0.09em;
  }

  h2 {
    font-size: 24px;
    font-weight: 400;
    line-height: 140%;
    letter-spacing: 0.05em;
  }

  h3 {
    font-size: 20px;
    font-weight: 300;
    line-height: 160%;
    letter-spacing: 0.03em;
  }

  h4 {
    font-size: 18px;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: 0.03em;
  }

  p {
    font-size: 18px;
    font-weight: 300;
    line-height: 150%;
    color: ${(p) => p.theme.colors.black};
    margin: 20px 0 0;
    &:first-of-type {
      margin-top: 10px;
    }
  }

  strong,
  b {
    font-weight: 500;
  }

  ul,
  ol {
    margin: 20px 0 0;
    padding-inline-start: 24px;
  }

  li {
    font-size: 18px;
    font-weight: 300;
    line-height: 150%;
    color: ${(p) => p.theme.colors.black};
    margin-top: 8px;
  }

  a {
    color: ${(p) => p.theme.colors.saphire};
    text-decoration: underline;

    &:focus-visible {
      outline: 2px solid ${(p) => p.theme.colors.mazdaRed};
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 20px 0 0;
  }

  /* Wide tables scroll inside their own container instead of stretching the page. */
  .${TABLE_SCROLL_CLASS} {
    margin: 20px 0 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  table {
    border-collapse: collapse;
    text-align: right;
    /* Keep columns readable while still allowing the wrapper to scroll. */
    min-width: 100%;
  }

  th,
  td {
    border: 1px solid ${(p) => p.theme.colors.borderGrey};
    padding: 10px 12px;
    font-size: 16px;
    font-weight: 300;
    line-height: 140%;
    color: ${(p) => p.theme.colors.black};
  }

  th {
    font-weight: 500;
    background: ${(p) => p.theme.colors.lightGrey};
  }

  ${(p) => p.theme.breakpoints.down("sm")} {
    h1 {
      font-size: 24px;
    }
    h2 {
      font-size: 20px;
    }
    h3,
    h4 {
      font-size: 18px;
    }
    p,
    li {
      font-size: 16px;
    }
  }
`

type Props = {
  /** Rich text HTML, already sanitized by the API (DLK-910) and prepared for render. */
  html: string
}

const ContentPage = ({ html }: Props) => {
  return (
    <StyledPageWrapper $padding={"0"}>
      <StyledPageDiv>
        <StyledTextWrapper>
          <StyledContent dir="rtl" dangerouslySetInnerHTML={{ __html: html }} />
        </StyledTextWrapper>
      </StyledPageDiv>
    </StyledPageWrapper>
  )
}

export default ContentPage
